<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ShopifyAPI from 'dashboard/api/integrations/shopify';
import ShopifyOrderItem from './ShopifyOrderItem.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const orders = ref([]);
const customer = ref(null);
const loading = ref(true);
const error = ref('');

const formatCurrency = (amount, currency) => {
  if (amount === undefined || amount === null) return '';
  try {
    const validCurrency =
      currency && typeof currency === 'string' && currency.length === 3
        ? currency.toUpperCase()
        : 'USD';
    const num = Number(amount);
    if (isNaN(num)) return `${amount} ${currency || ''}`.trim();
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: validCurrency,
    }).format(num);
  } catch (e) {
    return `${amount} ${currency || ''}`.trim();
  }
};

const fetchOrders = async () => {
  if (!props.contactId) {
    loading.value = false;
    return;
  }
  try {
    loading.value = true;
    error.value = '';
    const response = await ShopifyAPI.getOrders(props.contactId);
    if (Array.isArray(response.data)) {
      orders.value = response.data;
      customer.value = null;
    } else if (response.data && typeof response.data === 'object') {
      orders.value = response.data.orders || [];
      customer.value = response.data.customer || null;
    } else {
      orders.value = [];
      customer.value = null;
    }
  } catch (e) {
    error.value =
      e.response?.data?.error || t('CONVERSATION_SIDEBAR.SHOPIFY.ERROR');
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.contactId,
  newId => {
    if (newId) {
      fetchOrders();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="px-4 py-2 text-n-slate-12">
    <div v-if="loading" class="flex justify-center items-center p-4">
      <Spinner size="32" class="text-n-brand" />
    </div>
    <div v-else-if="error" class="text-center text-n-ruby-11 py-2 text-xs">
      {{ error }}
    </div>
    <div v-else-if="!orders.length" class="text-center text-n-slate-11 py-2 text-xs">
      {{ $t('CONVERSATION_SIDEBAR.SHOPIFY.NO_SHOPIFY_ORDERS') }}
    </div>
    <div v-else class="flex flex-col gap-3">
      <!-- Customer Lifetime Summary Banner -->
      <div
        v-if="customer && (customer.orders_count || customer.total_spent)"
        class="p-2.5 rounded-lg bg-n-slate-2 dark:bg-n-solid-2 border border-n-weak flex items-center justify-between text-xs"
      >
        <div class="flex items-center gap-1.5 font-medium text-n-slate-12">
          <i class="i-lucide-user text-n-slate-10 text-sm flex-shrink-0" />
          <span>{{ $t('CONVERSATION_SIDEBAR.SHOPIFY.CUSTOMER_OVERVIEW') }}</span>
        </div>
        <div class="flex items-center gap-1.5 text-n-slate-11 flex-shrink-0">
          <span v-if="customer.orders_count !== undefined">
            {{
              customer.orders_count === 1
                ? $t('CONVERSATION_SIDEBAR.SHOPIFY.SINGLE_ORDER_COUNT')
                : $t('CONVERSATION_SIDEBAR.SHOPIFY.ORDERS_COUNT', {
                    count: customer.orders_count,
                  })
            }}
          </span>
          <span v-if="customer.total_spent" class="font-medium text-n-slate-12">
            • {{ formatCurrency(customer.total_spent, customer.currency) }}
          </span>
        </div>
      </div>

      <!-- Orders List -->
      <div class="flex flex-col divide-y divide-n-weak">
        <ShopifyOrderItem
          v-for="order in orders"
          :key="order.id"
          :order="order"
        />
      </div>
    </div>
  </div>
</template>
